import { useCallback, useState } from 'react'
import am5locales_en_US from '@amcharts/amcharts5/locales/en_US'
import am5locales_ko_KR from '@amcharts/amcharts5/locales/ko_KR'

// Import local translation files
import enTranslations from '@/locales/translation/en.json'
import koTranslations from '@/locales/translation/ko-KR.json'
import enNotificationTranslations from '@/locales/notification/en.json'
import koNotificationTranslations from '@/locales/notification/ko-KR.json'
import { isNil } from 'lodash'

export const SUPPORTED_LANGUAGES = {
  en: {
    code: 'en',
    name: 'English',
    flag: '🇺🇸',
  },
  'ko-KR': {
    code: 'ko-KR',
    name: '한국어',
    flag: '🇰🇷',
  },
} as const

export type SupportedLanguage = keyof typeof SUPPORTED_LANGUAGES

type TranslationMap = Record<string, any>

const localTranslations: Record<SupportedLanguage, TranslationMap> = {
  'en': {
    ...(enTranslations as unknown as TranslationMap),
    ...(enNotificationTranslations as unknown as TranslationMap),
  },
  'ko-KR': {
    ...(koTranslations as unknown as TranslationMap),
    ...(koNotificationTranslations as unknown as TranslationMap),
  },
}

const LANGUAGE_STORAGE_KEY = 'app_language'

const getSavedLanguage = (): SupportedLanguage => {
  try {
    const savedLang = localStorage.getItem(LANGUAGE_STORAGE_KEY)
    if (savedLang && Object.keys(SUPPORTED_LANGUAGES).includes(savedLang)) {
      return savedLang as SupportedLanguage
    }
    if (savedLang === 'ko') {
      return 'ko-KR'
    }
  } catch (error) {
    // Fallback silently
  }
  return 'en'
}

const saveLanguage = (language: SupportedLanguage): void => {
  try {
    localStorage.setItem(LANGUAGE_STORAGE_KEY, language)
  } catch (error) {
    // Silently handle localStorage error
  }
}

export function useTranslation() {
  const [currentLanguage, setCurrentLanguage] = useState<SupportedLanguage>(getSavedLanguage())

  const getNestedValue = (obj: any, path: string): string | undefined => {
      const value = path.split('.').reduce((current, key) => current?.[key], obj)
      return typeof value === 'string' ? value : undefined
  }

  const interpolate = (template: string, vars?: Record<string, any>): string => {
      if (!template) return template
      if (!vars) return template
    return template.replace(/\{\{?\s*(\w+)\s*\}?\}/g, (match, k) => {
        const v = vars[k]
      return v === undefined || v === null ? match : String(v)
      })
  }

  const t = useCallback((key: string, options?: Record<string, any>): string => {
      const translations = localTranslations[currentLanguage]

      if (translations) {
        // First try direct key lookup (for dot-notation keys like "programs.globalExclusionTime")
        let value = translations[key]

        // If not found, try nested lookup
        if (!value) {
          value = getNestedValue(translations, key)
        }

        if (value && !isNil(value) && typeof value === 'string') {
          return interpolate(value, options)
        }
      }

      // Fallback to English if current language is not English
      if (currentLanguage !== 'en') {
        let enValue = localTranslations['en'][key]

        if (!enValue) {
          enValue = getNestedValue(localTranslations['en'], key)
        }

        if (enValue && !isNil(enValue) && typeof enValue === 'string') {
          return interpolate(enValue, options)
        }
      }

      return key
  }, [currentLanguage])

  const switchLanguage = useCallback(async (language: SupportedLanguage) => {
    saveLanguage(language)
    setCurrentLanguage(language)
    return Promise.resolve()
  }, [])

  const languageInfo = SUPPORTED_LANGUAGES[currentLanguage]

  const amChartLocale = useCallback(() => {
    switch (currentLanguage) {
      case 'en':
        return am5locales_en_US
      case 'ko-KR':
        return am5locales_ko_KR
      default:
        return am5locales_en_US
    }
  }, [currentLanguage])

  return {
    t,
    switchLanguage,
    currentLanguage,
    languageInfo,
    ready: true,
    amChartLocale,
  }
}

export default useTranslation
